// PATH: src/components/chapters/ChaptersTable.tsx
import * as React from "react";
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import type { ChapterSourcePart } from "../../api/documents/documents";
import DataObjectIcon from "@mui/icons-material/DataObject";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import type { Document } from "../../models/domain";
import { StatusPill } from "../ui";
import { formatChapterPosition } from "../documents/documentChapter";
import type { CurriculumNames } from "./chapterFilters";
import type { OssatureChapterItem } from "../../api/coverage/coverage";
import IssuePills from "../coverage/IssuePills";
import { formatLessonsReady } from "../coverage/chapterIssues";
import { REVIEW_LABELS, REVIEW_TONES, asReviewStatus, showReviewPill } from "./reviewStatus";

/** Ligne de `GET /v1/documents` : le document et son nombre d'objectifs. */
export type ChapterRow = Document & { objectives_count?: number };

export interface ChapterColumns {
  grade: boolean;
  subject: boolean;
  track: boolean;
}

interface Props {
  rows: ChapterRow[];
  loading: boolean;
  columns: ChapterColumns;
  names: CurriculumNames;
  selectedId?: string | null;
  /** Complétude par document (objectifs de la dernière version, défauts) ; absente = ancien affichage. */
  completeness?: Map<string, OssatureChapterItem> | null;
  onOpen: (row: ChapterRow) => void;
  /** Clic sur le badge de relecture : ouvre le chapitre sur sa relecture. */
  onOpenReview?: (row: ChapterRow) => void;
  onEdit: (row: ChapterRow) => void;
  onDownload: (row: ChapterRow, part: ChapterSourcePart) => void;
  onDelete: (row: ChapterRow) => void;
  /** Interrupteur « ouvert aux élèves » du cours (confirmation à la charge de l'appelant). */
  onToggleActive: (row: ChapterRow) => void;
  /** Ligne dont l'interrupteur est en cours d'écriture. */
  togglingId?: string | null;
  /** Relecteur : ni « Modifier » ni « Supprimer », ni interrupteur ; les téléchargements restent. */
  readOnly?: boolean;
}

const dateFmt = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" });
const formatDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : dateFmt.format(d);
};

/** Liste des chapitres, dans l'ordre du programme. */
export const ChaptersTable: React.FC<Props> = ({
  rows,
  loading,
  columns,
  names,
  selectedId,
  completeness,
  onOpen,
  onOpenReview,
  onEdit,
  onDownload,
  onDelete,
  onToggleActive,
  togglingId,
  readOnly = false,
}) => {
  const [menu, setMenu] = React.useState<{ anchor: HTMLElement; row: ChapterRow } | null>(null);
  const closeMenu = () => setMenu(null);
  const act = (fn: (row: ChapterRow) => void) => () => {
    if (menu) fn(menu.row);
    closeMenu();
  };

  const colCount = 5 + Number(columns.grade) + Number(columns.subject) + Number(columns.track);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <TableContainer sx={{ borderRadius: 0 }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 2 }}>Chapitre</TableCell>
              {columns.grade && <TableCell>Niveau</TableCell>}
              {columns.subject && <TableCell>Matière</TableCell>}
              {columns.track && <TableCell>Filière</TableCell>}
              <TableCell>Objectifs</TableCell>
              <TableCell>État</TableCell>
              <TableCell>Importé le</TableCell>
              <TableCell align="right" sx={{ pr: 2 }} aria-label="Actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? [0, 1, 2, 3, 4].map((i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={colCount} sx={{ px: 2 }}>
                      <Skeleton variant="text" width={i % 2 ? "55%" : "40%"} />
                      <Skeleton variant="text" width="20%" />
                    </TableCell>
                  </TableRow>
                ))
              : rows.map((row) => {
                  const position = formatChapterPosition(row);
                  const count = row.objectives_count;
                  const item = completeness?.get(row.id);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={row.id === selectedId}
                      onClick={() => onOpen(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell sx={{ pl: 2, minWidth: 260 }}>
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                          <Typography
                            variant="body2"
                            className="mono"
                            color="text.secondary"
                            sx={{ minWidth: 52, pt: 0.25, whiteSpace: "nowrap" }}
                            title="Semestre · ordre"
                          >
                            {position || "—"}
                          </Typography>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body1" fontWeight={600} dir="auto">
                              {row.title}
                            </Typography>
                            {row.chapter_ref && (
                              <Typography variant="caption" className="mono" color="text.secondary">
                                {row.chapter_ref}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      {columns.grade && <TableCell sx={{ whiteSpace: "nowrap" }}>{names.grade(row.grade_id)}</TableCell>}
                      {columns.subject && <TableCell>{names.subject(row.subject_id)}</TableCell>}
                      {columns.track && (
                        <TableCell sx={{ color: row.track_id ? "text.primary" : "text.secondary" }}>
                          {names.track(row.track_id)}
                        </TableCell>
                      )}
                      <TableCell sx={{ minWidth: 170, maxWidth: 280 }}>
                        {item ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                              {item.objectives}
                              <Typography component="span" variant="caption" color="text.secondary">
                                {" "}
                                · {item.objectives_with_facts} avec faits
                              </Typography>
                            </Typography>
                            {formatLessonsReady(item) && (
                              <Typography variant="caption" color="text.secondary" component="div">
                                {formatLessonsReady(item)}
                              </Typography>
                            )}
                            <Box sx={{ mt: 0.5 }}>
                              {/* L'inactivité est dite dans la colonne État. */}
                              <IssuePills
                                chapter={{ ...item, is_active: row.is_active }}
                                exclude={["inactive"]}
                                empty={
                                  <Typography variant="caption" color="text.secondary">
                                    Complet
                                  </Typography>
                                }
                              />
                            </Box>
                          </Box>
                        ) : /* `objectives_count` compte toutes les versions : on n'en
                            garde que « extraits ou non », le nombre exact est dans le détail. */
                        count == null ? (
                          "—"
                        ) : count === 0 ? (
                          <StatusPill tone="warning" title="Aucun objectif extrait : le chapitre n'est pas jouable">
                            à extraire
                          </StatusPill>
                        ) : (
                          <StatusPill tone="success">extraits</StatusPill>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                          {readOnly ? (
                            <StatusPill tone={row.is_active ? "success" : "neutral"}>
                              {row.is_active ? "Actif" : "Inactif"}
                            </StatusPill>
                          ) : (
                            <Tooltip title={row.is_active ? "Ouvert aux élèves — fermer" : "Fermé aux élèves — ouvrir"}>
                              <Box
                                component="span"
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                              >
                                <Switch
                                  size="small"
                                  checked={row.is_active}
                                  disabled={togglingId === row.id}
                                  onChange={() => onToggleActive(row)}
                                  inputProps={{ "aria-label": `Chapitre ${row.title} ouvert aux élèves` }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {row.is_active ? "Actif" : "Inactif"}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {/* Cours actif, matière fermée : l'élève ne le voit pas. */}
                          {row.subject_active === false && (
                            <StatusPill title="La matière est fermée aux élèves (Catalogue) : ce cours n'est pas servi">
                              Matière fermée
                            </StatusPill>
                          )}
                          {showReviewPill(row.review_status) && (
                            <Box
                              component="button"
                              type="button"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                (onOpenReview ?? onOpen)(row);
                              }}
                              aria-label={`Relecture : ${REVIEW_LABELS[asReviewStatus(row.review_status)]}`}
                              sx={{ p: 0, border: 0, bgcolor: "transparent", cursor: "pointer", font: "inherit" }}
                            >
                              <StatusPill
                                tone={REVIEW_TONES[asReviewStatus(row.review_status)]}
                                title="Relecture humaine du chapitre"
                              >
                                {REVIEW_LABELS[asReviewStatus(row.review_status)]}
                              </StatusPill>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                        {formatDate(row.created_at)}
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 1.5, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                        <Button size="small" variant="outlined" onClick={() => onOpen(row)}>
                          {readOnly ? "Relire" : "Contrôler"}
                        </Button>
                        <Tooltip title="Plus d'actions">
                          <IconButton
                            size="small"
                            sx={{ ml: 0.5 }}
                            aria-label={`Actions pour ${row.title}`}
                            onClick={(e) => setMenu({ anchor: e.currentTarget, row })}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={act((r) => onDownload(r, "markdown"))}>
          <ListItemIcon>
            <ArticleOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Télécharger le Markdown</ListItemText>
        </MenuItem>
        <MenuItem onClick={act((r) => onDownload(r, "manifest"))}>
          <ListItemIcon>
            <DataObjectIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Télécharger le manifeste</ListItemText>
        </MenuItem>
        {!readOnly && (
          <MenuItem onClick={act(onEdit)}>
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Modifier</ListItemText>
          </MenuItem>
        )}
        {!readOnly && (
          <MenuItem onClick={act(onDelete)} sx={{ color: "error.dark" }}>
            <ListItemIcon sx={{ color: "inherit" }}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Supprimer</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Paper>
  );
};

export default ChaptersTable;
